import { EntityModel } from '@/interfaces'
import {
  ALL_BOOKINGS_QUERY,
  CREATE_BOOKING_MUTATION,
  DELETE_BOOKING_MUTATION,
  FORCE_DELETE_BOOKING_MUTATION,
  SINGLE_BOOKING_QUERY,
  UPDATE_BOOKING_MUTATION,
} from './graphql'
import travelEntity from '@/entities/travel/entity'
import ratioTravelEntity from '@/entities/ratioTravel/entity'
import ratioTravelDateEntity from '@/entities/ratioTravelDate/entity'
import travellerEntity from '@/entities/traveller/entity'
import onlinePaymentEntity from '@/entities/onlinePayment/entity'
import tenantEntity from '@/entities/tenant/entity'

const bookingModel: EntityModel = {
  name: 'booking',
  namePlural: 'bookings',
  uniqueName: 'booking',
  uniqueNamePlural: 'bookings',
  path: 'travel/booking/booking',
  baseUrl: '/travel/booking/bookings',
  translationPrefix: 'entities:booking.',
  getDisplayName: (values: any) => {
    return 'Booking'
  },
  gql: {
    getAll: ALL_BOOKINGS_QUERY,
    getSingle: SINGLE_BOOKING_QUERY,
    create: CREATE_BOOKING_MUTATION,
    update: UPDATE_BOOKING_MUTATION,
    delete: DELETE_BOOKING_MUTATION,
    forceDelete: FORCE_DELETE_BOOKING_MUTATION,
  },
  attributes: {
    referenceNo: {
      name: 'referenceNo',
      label: 'entities:booking.attributes.referenceNo.label',
      type: 'value',
      dataType: 'string',
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    travel: {
      name: 'travel',
      label: 'entities:booking.attributes.travel.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return travelEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    pricingTravel: {
      name: 'pricingTravel',
      label: 'entities:booking.attributes.pricingTravel.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return ratioTravelEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    travelDate: {
      name: 'travelDate',
      label: 'entities:booking.attributes.travelDate.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return ratioTravelDateEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    travellers: {
      name: 'travellers',
      label: 'entities:booking.attributes.travellers.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travellerEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    travellerRemarks: {
      name: 'travellerRemarks',
      label: 'entities:booking.attributes.travellerRemarks.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'textarea',
      validationType: 'string',
      validations: [],
    },
    travellerDataCorrectnessConfirmation: {
      name: 'travellerDataCorrectnessConfirmation',
      label: 'entities:booking.attributes.travellerDataCorrectnessConfirmation.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'checkbox',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    localState: {
      name: 'localState',
      label: 'entities:booking.attributes.localState.label',
      type: 'value',
      dataType: '{ [x: string]: any }',
      defaultInput: true,
      inputType: 'textarea',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    backofficePayload: {
      name: 'backofficePayload',
      label: 'entities:booking.attributes.backofficePayload.label',
      type: 'value',
      dataType: '{ [x: string]: any }',
      defaultInput: true,
      inputType: 'textarea',
      validationType: 'string',
      validations: [],
    },
    backofficeResponse: {
      name: 'backofficeResponse',
      label: 'entities:booking.attributes.backofficeResponse.label',
      type: 'value',
      dataType: '{ [x: string]: any }',
      defaultInput: true,
      inputType: 'textarea',
      validationType: 'string',
      validations: [],
    },
    transmissionStatus: {
      name: 'transmissionStatus',
      label: 'entities:booking.attributes.transmissionStatus.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'select',
      selectOptions: [
        {
          label: 'entities:booking.attributes.transmissionStatus.selectOptions.PENDING.label',
          value: 'PENDING',
        },
        {
          label: 'entities:booking.attributes.transmissionStatus.selectOptions.SUCCESS.label',
          value: 'SUCCESS',
        },
        {
          label: 'entities:booking.attributes.transmissionStatus.selectOptions.ERROR.label',
          value: 'ERROR',
        },
      ],
      validationType: 'string',
      validations: [],
    },
    webhookStatus: {
      name: 'webhookStatus',
      label: 'entities:booking.attributes.webhookStatus.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    webhookResponse: {
      name: 'webhookResponse',
      label: 'entities:booking.attributes.webhookResponse.label',
      type: 'value',
      dataType: '{ [x: string]: any }',
      defaultInput: true,
      inputType: 'textarea',
      validationType: 'string',
      validations: [],
    },
    webhookDuration: {
      name: 'webhookDuration',
      label: 'entities:booking.attributes.webhookDuration.label',
      type: 'value',
      dataType: 'number',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    onlinePayments: {
      name: 'onlinePayments',
      label: 'entities:booking.attributes.onlinePayments.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return onlinePaymentEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    confirmationEmailSent: {
      name: 'confirmationEmailSent',
      label: 'entities:booking.attributes.confirmationEmailSent.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'datetime',
      validationType: 'string',
      validations: [],
    },
    tenant: {
      name: 'tenant',
      label: 'entities:booking.attributes.tenant.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return tenantEntity
      },
      defaultInput: true,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    isPassportDataRequired: {
      name: 'isPassportDataRequired',
      label: 'entities:booking.attributes.isPassportDataRequired.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    passportDataReminderEmailSent: {
      name: 'passportDataReminderEmailSent',
      label: 'entities:booking.attributes.passportDataReminderEmailSent.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'datetime',
      validationType: 'string',
      validations: [],
    },
    passportDataReceived: {
      name: 'passportDataReceived',
      label: 'entities:booking.attributes.passportDataReceived.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'datetime',
      validationType: 'string',
      validations: [],
    },
  },
}

export default bookingModel
